import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { StatusBadge, SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_GROUPS = [
  { value: "all", label: "All orders" },
  { value: "active", label: "Active", match: (s) => !["dispatched", "in_transit", "delivered", "pod_received", "closed", "cancelled"].includes(s) },
  { value: "dispatch", label: "Dispatched / Delivery", match: (s) => ["dispatched", "in_transit", "delivered", "pod_received"].includes(s) },
  { value: "closed", label: "Closed", match: (s) => s === "closed" },
];

function priorityTextClass(priority) {
  if (priority === "urgent") return "text-signal-red";
  if (priority === "high") return "text-signal-amber";
  return "text-muted-foreground";
}

export default function OrderFulfilment() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer: "", sku: "", item_name: "", quantity: "", cartons: "", required_date: "", priority: "normal" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = ["admin", "warehouse_manager", "supervisor", "sales"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/orders");
    setOrders(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/orders", {
        ...form,
        quantity: parseInt(form.quantity) || 0,
        cartons: form.cartons ? parseInt(form.cartons) : null,
        required_date: form.required_date || null,
      });
      toast.success("Sales order created");
      setOpen(false);
      setForm({ customer: "", sku: "", item_name: "", quantity: "", cartons: "", required_date: "", priority: "normal" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to create order");
    }
  };

  const g = STATUS_GROUPS.find((x) => x.value === group);
  const filtered = orders.filter(
    (o) =>
      (group === "all" || g.match(o.fulfilment_status)) &&
      (o.bill_no.toLowerCase().includes(search.toLowerCase()) ||
        o.customer.toLowerCase().includes(search.toLowerCase()) ||
        o.sku.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="orders-page">
      <SectionTitle
        right={
          canCreate && (
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate("/orders/new")} data-testid="new-order-button">
              <Plus className="w-4 h-4 mr-1" /> New Order
            </Button>
          )
        }
      >
        Order Fulfilment
      </SectionTitle>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative max-w-sm flex-1 min-w-48">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="orders-search-input" className="pl-9" placeholder="Search order / customer / SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {STATUS_GROUPS.map((sg) => (
          <button
            key={sg.value}
            onClick={() => setGroup(sg.value)}
            data-testid={`orders-filter-${sg.value}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${group === sg.value ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
          >
            {sg.label}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Order No", "Customer", "SKU", "Qty", "Priority", "Required By", "Fulfilment Status", "Created"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.bill_no} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer transition-colors duration-150" onClick={() => navigate(`/orders/${o.bill_no}`)} data-testid={`order-row-${o.bill_no}`}>
                <td className="px-4 py-3 metric font-bold text-primary whitespace-nowrap">{o.bill_no}</td>
                <td className="px-4 py-3">{o.customer}</td>
                <td className="px-4 py-3 text-muted-foreground">{o.sku}</td>
                <td className="px-4 py-3 metric">{o.quantity.toLocaleString()}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase tracking-widest ${priorityTextClass(o.priority)}`}>{o.priority}</span></td>
                <td className="px-4 py-3 metric text-xs">{o.required_date ? new Date(o.required_date).toLocaleDateString([], { day: "2-digit", month: "short" }) : "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={o.fulfilment_status} /></td>
                <td className="px-4 py-3 text-muted-foreground metric text-xs">{fmtDT(o.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No orders found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
